import React from "react";
import { KpiCard, Panel, StatRow } from "@/pages/dashboard/widgets";
import { inr } from "@/lib/dashboard";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";

export default function ExecutiveView({ data }) {
  if (!data) return null;
  const s = data.sales || {};
  const p = data.production || {};
  const q = data.qc || {};
  const d = data.dispatch || {};

  return (
    <div className="space-y-6" data-testid="exec-view">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard testid="exec-kpi-so-value" label="Open order value" value={inr(s.sales_order_value)} accent="text-indigo-600" />
        <KpiCard testid="exec-kpi-conversion" label="Quote conversion" value={`${s.quotation_conversion?.rate ?? 0}%`} sub={`${s.quotation_conversion?.accepted ?? 0}/${s.quotation_conversion?.total ?? 0} accepted`} accent="text-sky-600" />
        <KpiCard testid="exec-kpi-qc" label="QC pass rate" value={`${q.pass_rate ?? 0}%`} accent="text-emerald-600" />
        {data.finance
          ? <KpiCard testid="exec-kpi-receivable" label="Receivables" value={inr(data.finance.receivable)} accent="text-rose-600" />
          : <KpiCard testid="exec-kpi-prod" label="Machine utilisation" value={`${p.machine_utilisation ?? 0}%`} accent="text-amber-600" />}
      </div>

      {data.finance && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KpiCard testid="exec-kpi-revenue" label="Revenue (posted)" value={inr(data.finance.revenue)} accent="text-emerald-600" />
          <KpiCard testid="exec-kpi-collections" label="Today's collections" value={inr(data.finance.todays_collections)} accent="text-indigo-600" />
          <KpiCard testid="exec-kpi-overdue" label="Overdue invoices" value={data.finance.overdue_invoices ?? 0} accent="text-amber-600" />
          {data.inventory && <KpiCard testid="exec-kpi-valuation" label="Stock valuation" value={inr(data.inventory.valuation)} accent="text-slate-900" />}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Revenue trend (6 months)">
          <div className="h-56" data-testid="exec-revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.revenue_trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={(m) => m?.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => inr(v)} />
                <Line type="monotone" dataKey="revenue" stroke="#4f46e5" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title="Sales funnel">
          <div className="h-56" data-testid="exec-funnel-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={s.funnel || []} layout="vertical" margin={{ left: 20 }}>
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="stage" tick={{ fontSize: 11 }} width={90} />
                <Tooltip />
                <Bar dataKey="count" fill="#4f46e5" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="Production today (m)">
          <StatRow items={[
            { label: "Printed", value: p.today_meters?.printing ?? 0, testid: "exec-printed" },
            { label: "Fused", value: p.today_meters?.fusing ?? 0, testid: "exec-fused" },
            { label: "Cut", value: p.today_meters?.cutting ?? 0, testid: "exec-cut" },
            { label: "Stitched", value: p.today_meters?.stitching ?? 0, testid: "exec-stitched" },
            { label: "Bundled", value: p.today_meters?.packing ?? 0, testid: "exec-packed" },
          ]} />
        </Panel>
        <Panel title="Dispatch">
          <StatRow items={[
            { label: "Ready", value: d.orders_ready ?? 0 },
            { label: "In transit", value: d.pending_deliveries ?? 0 },
            { label: "Today", value: d.todays_dispatches ?? 0, accent: "text-indigo-600" },
          ]} />
        </Panel>
        <Panel title="Alerts" right={<Badge className="bg-amber-50 text-amber-700 hover:bg-amber-50">{(data.alerts || []).length}</Badge>}>
          <div className="space-y-2" data-testid="exec-alerts">
            {(data.alerts || []).map((a, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-slate-600"><AlertTriangle className={`mt-0.5 h-4 w-4 ${a.level === "warning" ? "text-amber-500" : "text-sky-500"}`} />{a.message}</div>
            ))}
            {(data.alerts || []).length === 0 && <p className="text-sm text-slate-400">No alerts. All clear.</p>}
          </div>
        </Panel>
      </div>
    </div>
  );
}
